import type { RigParams } from '@alter/avatar-core';

/**
 * Traducción `RigParams` → pesos de blend shapes ARKit.
 *
 * Los 11 parámetros canónicos de `avatar-core` son deliberadamente más
 * gruesos que los 51 blend shapes del avatar: son el contrato que todo
 * avatar cumple, no la capacidad máxima del avatar. Este módulo es la
 * única pieza que conoce ambos vocabularios.
 *
 * La tabla y sus coeficientes vienen de `docs/07_BLENDSHAPES_ARKIT.md` §4.
 * Son **valores iniciales de calibración**, no verdad revelada: se
 * ajustan mirando el avatar (F0.6 paso 4). Vive en el adapter y no en
 * `avatar-core` justamente para que calibrar no toque el motor.
 */

/** Los 51 blend shapes que expone `model.glb` (ARKit menos `tongueOut`). */
export const ARKIT_SHAPES = [
  'browDownLeft',
  'browDownRight',
  'browInnerUp',
  'browOuterUpLeft',
  'browOuterUpRight',
  'cheekPuff',
  'cheekSquintLeft',
  'cheekSquintRight',
  'eyeBlinkLeft',
  'eyeBlinkRight',
  'eyeLookDownLeft',
  'eyeLookDownRight',
  'eyeLookInLeft',
  'eyeLookInRight',
  'eyeLookOutLeft',
  'eyeLookOutRight',
  'eyeLookUpLeft',
  'eyeLookUpRight',
  'eyeSquintLeft',
  'eyeSquintRight',
  'eyeWideLeft',
  'eyeWideRight',
  'jawForward',
  'jawLeft',
  'jawOpen',
  'jawRight',
  'mouthClose',
  'mouthDimpleLeft',
  'mouthDimpleRight',
  'mouthFrownLeft',
  'mouthFrownRight',
  'mouthFunnel',
  'mouthLeft',
  'mouthLowerDownLeft',
  'mouthLowerDownRight',
  'mouthPressLeft',
  'mouthPressRight',
  'mouthPucker',
  'mouthRight',
  'mouthRollLower',
  'mouthRollUpper',
  'mouthShrugLower',
  'mouthShrugUpper',
  'mouthSmileLeft',
  'mouthSmileRight',
  'mouthStretchLeft',
  'mouthStretchRight',
  'mouthUpperUpLeft',
  'mouthUpperUpRight',
  'noseSneerLeft',
  'noseSneerRight',
] as const;

export type ArkitShape = (typeof ARKIT_SHAPES)[number];

/** Mapa disperso de peso por shape. Ausente = 0. */
export type ArkitWeights = Partial<Record<ArkitShape, number>>;

/** Posición de cada shape dentro del buffer de pesos. */
export const IDX_ARKIT: Record<ArkitShape, number> = Object.fromEntries(
  ARKIT_SHAPES.map((s, i) => [s, i]),
) as Record<ArkitShape, number>;

/**
 * Buffer reutilizable de una pose traducida.
 *
 * El render loop llama a la traducción **una vez por frame**. Con
 * objetos y `Object.entries` eso eran ~20 asignaciones por frame; a 80
 * FPS, 1.600 objetos por segundo que el GC tiene que recoger, y en
 * móvil eso aparece como un goteo constante de frames lentos — la
 * firma exacta que midió el Android en F0.6 paso 6. Con este buffer la
 * traducción no asigna nada.
 */
export interface ArkitPoseBuffer {
  /** Peso por shape, indexado por posición en `ARKIT_SHAPES`. */
  pesos: Float32Array;
  /** Se muta en el lugar, no se reemplaza. */
  gaze: GazeRotation;
}

export function crearPoseBuffer(): ArkitPoseBuffer {
  return { pesos: new Float32Array(ARKIT_SHAPES.length), gaze: { yaw: 0, pitch: 0 } };
}

/** Rotación de los huesos de ojo, en radianes. */
export interface GazeRotation {
  /** Rotación en Y (horizontal). Positivo = el avatar mira a su derecha. */
  yaw: number;
  /** Rotación en X (vertical). Positivo = el avatar mira arriba. */
  pitch: number;
}

export interface ArkitPose {
  weights: ArkitWeights;
  gaze: GazeRotation;
}

/** Amplitud máxima de giro ocular horizontal (`07` §4: ≈ v·20°). */
const GAZE_YAW_MAX_RAD = (20 * Math.PI) / 180;
/** Amplitud máxima de giro ocular vertical (`07` §4: ≈ v·15°). */
const GAZE_PITCH_MAX_RAD = (15 * Math.PI) / 180;

/**
 * Punto de `tension_mandib` a partir del cual la mueca recluta nariz y
 * labio superior. Por debajo, apretar es sólo mandíbula y ojos.
 *
 * Es el número que separa `DOLOR` de `ENOJO`, así que moverlo cambia
 * qué expresiones arrugan la nariz. La rampa es continua: no hay salto
 * al cruzarlo.
 */
const GRIMACE_THRESHOLD = 0.8;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const clampSigned = (v: number): number => (v < -1 ? -1 : v > 1 ? 1 : v);

/** Suma acumulando: varios `RigParams` pueden escribir el mismo shape. */
function add(pesos: Float32Array, shape: ArkitShape, value: number): void {
  if (value === 0) return;
  const i = IDX_ARKIT[shape];
  pesos[i] = (pesos[i] ?? 0) + value;
}

/**
 * Traduce la pose canónica a pesos ARKit + rotación de ojos.
 *
 * `intensidad` multiplica **todo** el resultado, incluida la mirada. Que
 * escale la mirada es discutible semánticamente, pero mantener la
 * rotación del hueso y el peso de los `eyeLook*` en la misma escala es
 * obligatorio: si se desincronizan, el párpado no acompaña al globo y la
 * mirada se ve "de muñeco" (`07` §3.3). Es una de las cosas a resolver
 * en la calibración de F0.6.
 */
export function escribirArkit(params: RigParams, out: ArkitPoseBuffer): void {
  const w = out.pesos;
  w.fill(0);

  const cejaIzq = clampSigned(params.ceja_izq);
  const cejaDer = clampSigned(params.ceja_der);
  const cejaInterna = clamp01(params.ceja_interna);
  const comIzq = clampSigned(params.comisura_izq);
  const comDer = clampSigned(params.comisura_der);
  const parpIzq = clamp01(params.parpado_izq);
  const parpDer = clamp01(params.parpado_der);
  const tension = clamp01(params.tension_mandib);
  const miradaX = clampSigned(params.mirada_x);
  const miradaY = clampSigned(params.mirada_y);

  // --- Cejas ---
  // Dos ejes ortogonales, porque son dos músculos distintos y opuestos:
  // `ceja_*` mueve el extremo externo (abajo = enojo, arriba = sorpresa)
  // y `ceja_interna` eleva el centro (tristeza, miedo). "Ceja arriba"
  // completa es la suma de los dos; la ceja triste es sólo el segundo.
  //
  // Hasta la calibración de F0.6, `ceja_*` positivo aportaba la mitad a
  // `browInnerUp` y no existía `ceja_interna`. Eso hacía imposible la
  // ceja triste — bajar `ceja_*` daba `browDown*`, que es enojo.
  if (cejaIzq < 0) add(w, 'browDownLeft', -cejaIzq);
  else add(w, 'browOuterUpLeft', cejaIzq);
  if (cejaDer < 0) add(w, 'browDownRight', -cejaDer);
  else add(w, 'browOuterUpRight', cejaDer);
  add(w, 'browInnerUp', cejaInterna);

  // --- Párpados ---
  // `parpado` es apertura (1 = abierto), `eyeBlink` es cierre: invertido.
  //
  // `eyeWide*` queda FUERA de este mapeo: "ojo muy abierto" necesita
  // headroom por encima del neutro y el rango 0..1 con neutro en 1 no lo
  // tiene. Se resuelve ampliando `RigParams` o con una regla de
  // traducción por expresión — decisión de calibración (paso 4), no de
  // esta capa. Ver `07_BLENDSHAPES_ARKIT.md` §4, que documenta el
  // hallazgo, y §4.1, donde `eyeWide*` figura como capacidad disponible.
  add(w, 'eyeBlinkLeft', 1 - parpIzq);
  add(w, 'eyeBlinkRight', 1 - parpDer);

  // --- Comisuras ---
  if (comIzq > 0) add(w, 'mouthSmileLeft', comIzq);
  else add(w, 'mouthFrownLeft', -comIzq);
  if (comDer > 0) add(w, 'mouthSmileRight', comDer);
  else add(w, 'mouthFrownRight', -comDer);

  // --- Tensión mandibular ---
  // Distribuida en cinco canales: apretar no es sólo mandíbula.
  //
  // CALIBRACIÓN F0.6 ronda 2 — `eyeSquint*` sube de 0.3 a 0.55. Es el
  // único canal del contrato que aprieta los ojos sin cerrarlos.
  // `DOLOR` intentaba conseguir ese efecto con `parpado: 0.5`, pero
  // `parpado` sólo se traduce a `eyeBlink`: bajarlo no aprieta el ojo,
  // lo cierra, y la cara pasaba a leerse como desvanecimiento en vez de
  // dolor. Apretar la mandíbula entrecierra los ojos de verdad, así que
  // el efecto vive acá y no en `parpado`.
  if (tension > 0) {
    add(w, 'jawForward', tension * 0.4);
    add(w, 'mouthPressLeft', tension * 0.7);
    add(w, 'mouthPressRight', tension * 0.7);
    add(w, 'mouthStretchLeft', tension * 0.3);
    add(w, 'mouthStretchRight', tension * 0.3);
    add(w, 'eyeSquintLeft', tension * 0.55);
    add(w, 'eyeSquintRight', tension * 0.55);
  }

  // --- Mueca: la tensión extrema recluta el tercio medio de la cara ---
  //
  // Apretar fuerte no es sólo mandíbula: pasado cierto punto entra el
  // elevador del labio superior y del ala de la nariz, que en ARKit son
  // `noseSneer*` y `mouthUpperUp*`. Es la mueca de `07` §3.4.
  //
  // Sirve para separar `DOLOR` del enojo, que compartían seis shapes y
  // sólo se diferenciaban en amplitud. Con la rampa, `DOLOR` (`tension`
  // 0.95) arruga la nariz y `ENOJO` (0.4) no.
  //
  // La regla mira el valor del parámetro, nunca qué expresión se
  // aplica — condición para no violar la regla del rig paramétrico
  // (`04_ARQUITECTURA.md` §6.4). Es la opción 1 de `07` §4.1: enriquecer
  // la traducción en vez de ampliar el contrato. Consecuencia querida:
  // cualquier expresión futura que pida tensión extrema recibe la mueca,
  // porque una cara real hace eso.
  const mueca =
    tension > GRIMACE_THRESHOLD ? (tension - GRIMACE_THRESHOLD) / (1 - GRIMACE_THRESHOLD) : 0;
  if (mueca > 0) {
    add(w, 'noseSneerLeft', mueca * 0.8);
    add(w, 'noseSneerRight', mueca * 0.8);
    add(w, 'mouthUpperUpLeft', mueca * 0.6);
    add(w, 'mouthUpperUpRight', mueca * 0.6);
  }

  // --- Intensidad global + clamp final ---
  const k = clamp01(params.intensidad);
  for (let i = 0; i < w.length; i++) {
    w[i] = clamp01((w[i] ?? 0) * k);
  }

  escribirMirada(miradaX * k, miradaY * k, out);
}

/** Los ocho `eyeLook*`: los únicos pesos que escribe `escribirMirada`. */
const MIRADA_SHAPES = [
  'eyeLookOutLeft',
  'eyeLookInRight',
  'eyeLookInLeft',
  'eyeLookOutRight',
  'eyeLookUpLeft',
  'eyeLookUpRight',
  'eyeLookDownLeft',
  'eyeLookDownRight',
] as const;

/**
 * Suma la mirada a una pose ya traducida: los `eyeLook*` sobre los pesos y
 * la rotación de los huesos de ojo en `out.gaze`.
 *
 * Está aparte de `escribirArkit` porque el adapter compone la mirada de
 * varias capas —expresión, idle, gestos— sobre la pose de la expresión, y
 * la mirada no es lineal en los pesos: mirar a la izquierda y después un
 * poco a la derecha no son `eyeLookOut` más `eyeLookIn`, es menos
 * `eyeLookOut`. Por eso se suma en unidades de `mirada_*` y se traduce
 * una sola vez.
 *
 * Recorta a ±1, que es ±20° de yaw y ±15° de pitch: los límites de
 * `06_ESTANDAR_RIG.md` §3.
 */
export function escribirMirada(x: number, y: number, out: ArkitPoseBuffer): void {
  const w = out.pesos;
  const miradaX = clampSigned(x);
  const miradaY = clampSigned(y);

  // Mirar a la izquierda del avatar = ojo izq hacia afuera + ojo der
  // hacia la nariz. Sin esto el globo gira pero la piel no acompaña.
  const absX = Math.abs(miradaX);
  if (miradaX < 0) {
    add(w, 'eyeLookOutLeft', absX);
    add(w, 'eyeLookInRight', absX);
  } else if (miradaX > 0) {
    add(w, 'eyeLookInLeft', absX);
    add(w, 'eyeLookOutRight', absX);
  }
  const absY = Math.abs(miradaY);
  if (miradaY > 0) {
    add(w, 'eyeLookUpLeft', absY);
    add(w, 'eyeLookUpRight', absY);
  } else if (miradaY < 0) {
    add(w, 'eyeLookDownLeft', absY);
    add(w, 'eyeLookDownRight', absY);
  }
  for (const s of MIRADA_SHAPES) {
    const i = IDX_ARKIT[s];
    w[i] = clamp01(w[i] ?? 0);
  }

  out.gaze.yaw = miradaX * GAZE_YAW_MAX_RAD;
  out.gaze.pitch = miradaY * GAZE_PITCH_MAX_RAD;
}

/**
 * Igual que `escribirArkit`, pero devolviendo un mapa disperso con los
 * shapes nombrados. **Asigna**, así que no va en el render loop: existe
 * para los tests y para el panel de la página de calibración, que se
 * repinta a 2 Hz.
 */
export function rigParamsToArkit(params: RigParams): ArkitPose {
  const buffer = crearPoseBuffer();
  escribirArkit(params, buffer);
  const weights: ArkitWeights = {};
  ARKIT_SHAPES.forEach((shape, i) => {
    const v = buffer.pesos[i] ?? 0;
    if (v !== 0) weights[shape] = v;
  });
  return { weights, gaze: { ...buffer.gaze } };
}
